import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import '../styles/StorySlider.css';

function StorySlider({ stories, title = "Latest Stories" }) {
  const [isAnimating, setIsAnimating] = useState(false);
  const [currentStories, setCurrentStories] = useState([]);
  const carouselRef = useRef(null);
  const autoPlayRef = useRef(null);
  const animationTimeoutRef = useRef(null);

  const timeRunning = 3000;
  const timeAutoNext = 7000;

  // Initialize stories order
  useEffect(() => {
    if (stories && stories.length > 0) {
      setCurrentStories([...stories]);
    }
  }, [stories]);

  // Auto-play functionality
  useEffect(() => {
    if (currentStories.length > 1) {
      autoPlayRef.current = setTimeout(() => {
        showSlider('next');
      }, timeAutoNext);
    }

    return () => {
      if (autoPlayRef.current) {
        clearTimeout(autoPlayRef.current);
      }
    };
  }, [currentStories, isAnimating]);

  const showSlider = (type) => {
    if (isAnimating || currentStories.length <= 1) return;

    setIsAnimating(true);
    const carousel = carouselRef.current;
    
    if (type === 'next') {
      // Move first story to end
      const newStories = [...currentStories];
      const firstStory = newStories.shift();
      newStories.push(firstStory);
      setCurrentStories(newStories);
      carousel.classList.add('next');
    } else {
      // Move last story to beginning
      const newStories = [...currentStories];
      const lastStory = newStories.pop();
      newStories.unshift(lastStory);
      setCurrentStories(newStories);
      carousel.classList.add('prev');
    }

    // Clear animation classes after animation completes
    if (animationTimeoutRef.current) {
      clearTimeout(animationTimeoutRef.current);
    }
    
    animationTimeoutRef.current = setTimeout(() => {
      carousel.classList.remove('next', 'prev');
      setIsAnimating(false);
    }, timeRunning);

    // Clear and reset auto-play
    if (autoPlayRef.current) {
      clearTimeout(autoPlayRef.current);
    }
  };

  const handleNext = () => {
    showSlider('next');
  };

  const handlePrev = () => {
    showSlider('prev');
  };

  const handleDotClick = (targetIndex) => {
    if (isAnimating || targetIndex === 0) return; // Current slide is always at index 0
    
    // Calculate how many moves we need to get the target slide to position 0
    const currentIndex = currentStories.findIndex(story => story === stories[targetIndex]);
    
    if (currentIndex !== -1) {
      // Move the target slide to the front
      const newStories = [...currentStories];
      for (let i = 0; i < currentIndex; i++) {
        const firstStory = newStories.shift();
        newStories.push(firstStory);
      }
      
      setIsAnimating(true);
      setCurrentStories(newStories);
      
      const carousel = carouselRef.current;
      carousel.classList.add('next');
      
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }
      
      animationTimeoutRef.current = setTimeout(() => {
        carousel.classList.remove('next');
        setIsAnimating(false);
      }, timeRunning);

      if (autoPlayRef.current) {
        clearTimeout(autoPlayRef.current);
      }
    }
  };

  if (!stories || stories.length === 0) {
    return null;
  }

  return (
    <div className="carousel" ref={carouselRef}>
      {/* Main list */}
      <div className="list">
        {currentStories.map((story, index) => (
          <div key={`${story.id}-${index}`} className="item">
            <img src={story.image} alt={story.title} />
            <div className="content">
              <div className="author">SUNNY SIDEUP STORYS</div>
              <div className="title">{story.title}</div>
              <div className="topic">HORROR STORY</div>
              <div className="des">{story.description}</div>
              <div className="buttons">
                <Link to={`/story/${story.id}`}>
                  <button>READ story</button>
                </Link>
              </div>
            </div>
          </div>
        ))}
      </div>


      {/* Navigation arrows */}
      <div className="arrows">
        <button id="prev" onClick={handlePrev} disabled={isAnimating}>
          &lt;
        </button>
        <button id="next" onClick={handleNext} disabled={isAnimating}>
          &gt;
        </button>
      </div>

      {/* Dot indicators */}
      <div className="dots-container">
        {stories.map((story, index) => (
          <button
            key={story.id}
            className={`dot ${currentStories[0]?.id === story.id ? 'active' : ''}`}
            onClick={() => handleDotClick(index)}
            disabled={isAnimating}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>

      {/* Progress bar */}
      <div className="time"></div>
    </div>
  );
}

export default StorySlider;